import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import {
  LayoutDashboard, Users2, Target, TrendingUp, ListChecks, BarChart3,
  Settings2, Sparkles, Palette, Bot, LogOut, Package, Building2,
  FileText, Zap, Globe, Clock, Rocket, Crosshair, Receipt,
  Briefcase, MessageSquarePlus, Bug, Mail, LineChart, ShieldCheck,
  MoreHorizontal, LucideIcon, X, ChevronsLeft, ChevronsRight,
  Sun, Moon, Eye, EyeOff, Brain, BookOpen, Code2
} from 'lucide-react';
import { authService } from '../../services/authService';
import { useDemoMode } from '../../contexts/DemoModeContext';
import { usePermissions } from '../../hooks/usePermissions';
import { PageKey } from '../../types/permissions';
import { moduleService } from '../../services/moduleService';
import { useIndustryWording } from '../../hooks/useIndustryWording';
import { googleAdsService } from '../../services/googleAdsService';
import { useIsMobile } from '../../hooks/useResponsive';
import { useNavigationConfig, type NavItemDef } from '../../hooks/useNavigationConfig';
import { NAV_GROUPS } from './navGroups';
import { useTheme } from '../../contexts/ThemeContext';
import { Modal, Button } from '@/components/shared';

// ─── Types ──────────────────────────────────────────────────

interface NavItem {
  id: string;
  icon: LucideIcon;
  label: string;
  special?: boolean;
}

interface SidebarProps {
  activeTab: string;
  onTabChange: (tab: string) => void;
  isOpen: boolean;
  onClose: () => void;
  currentUserName?: string;
  currentUser?: any;
  orgId?: string;
  currentUserId?: string;
  photoURL?: string;
  expanded?: boolean;
  onExpandedChange?: (expanded: boolean) => void;
  /** Bug / Änderung / Feature melden (Modal) — nicht die Ticket-Übersicht */
  onGiveFeedback?: () => void;
}

// ─── Component ──────────────────────────────────────────────

/**
 * Expandable Sidebar – Dark Canvas Design
 *
 * Desktop: Fixed sidebar, collapsible (68px) or expanded (220px) with toggle
 * Mobile: Full-screen overlay with labels, slide-in from left
 *
 * Architecture:
 * - Logo (top, gradient shine animation)
 * - Primary nav icons (core pages)
 * - Separator
 * - Secondary nav icons (tools)
 * - More button with viewport-aware popover
 * - User avatar (bottom, opens settings)
 */
const SidebarNew: React.FC<SidebarProps> = ({
  activeTab,
  onTabChange,
  isOpen,
  onClose,
  currentUserName,
  currentUser,
  orgId,
  currentUserId,
  photoURL,
  expanded: controlledExpanded,
  onExpandedChange,
  onGiveFeedback,
}) => {
  const { t } = useTranslation('navigation');
  const { t: tc } = useTranslation('common');
  const { permissions, canRead } = usePermissions(currentUser, orgId);
  const isMobile = useIsMobile();
  const { baseTheme, toggleBaseTheme, canToggleBaseTheme } = useTheme();
  useIndustryWording();

  const [isGoogleAdsConnected, setIsGoogleAdsConnected] = useState(false);
  const [internalExpanded, setInternalExpanded] = useState(false);
  const [moreOpen, setMoreOpen] = useState(false);
  const moreButtonRef = useRef<HTMLDivElement>(null);
  const morePopoverRef = useRef<HTMLDivElement>(null);

  const isExpanded = controlledExpanded ?? internalExpanded;
  const setExpanded = useCallback((val: boolean) => {
    if (onExpandedChange) onExpandedChange(val);
    else setInternalExpanded(val);
  }, [onExpandedChange]);

  useEffect(() => {
    const checkModules = async () => {
      if (!orgId) return;
      try {
        const googleAdsAccess = await moduleService.checkModuleAccess(orgId, 'google_ads');
        if (googleAdsAccess) {
          const conn = await googleAdsService.checkConnection(orgId);
          setIsGoogleAdsConnected(conn.isConnected && conn.status === 'connected');
        }
      } catch (e) {
        console.error('Module check error:', e);
      }
    };
    checkModules();
  }, [orgId]);

  useEffect(() => {
    if (!moreOpen || !moreButtonRef.current || !morePopoverRef.current) return;
    const btnRect = moreButtonRef.current.getBoundingClientRect();
    const popover = morePopoverRef.current;
    const viewportH = window.innerHeight;
    const popoverH = popover.scrollHeight;
    const sidebarWidth = isExpanded ? 220 : 68;
    const margin = 12;

    popover.style.left = `${sidebarWidth + 8}px`;

    if (btnRect.top + popoverH + margin > viewportH) {
      const bottomAligned = viewportH - margin;
      const topPos = Math.max(margin, bottomAligned - popoverH);
      popover.style.top = `${topPos}px`;
      popover.style.bottom = 'auto';
      popover.style.maxHeight = `${viewportH - topPos - margin}px`;
    } else {
      popover.style.top = `${btnRect.top}px`;
      popover.style.bottom = 'auto';
      popover.style.maxHeight = `${viewportH - btnRect.top - margin}px`;
    }
  }, [moreOpen, isExpanded]);

  const { isDemoMode, toggleDemoMode } = useDemoMode();
  const [signOutModalOpen, setSignOutModalOpen] = useState(false);

  const executeSignOut = async () => {
    setSignOutModalOpen(false);
    try {
      await authService.signOut();
    } catch (e) {
      console.error('Sign out error:', e);
    }
  };

  const handleNav = (id: string) => {
    onTabChange(id);
    if (isMobile) onClose();
  };

  // ─── Navigation Items (flat registry) ──────────────────────

  const iconMap: Record<string, LucideIcon> = {
    'dashboard': LayoutDashboard,
    'guide': BookOpen,
    'leads': Users2,
    'companies': Building2,
    'opportunities': Target,
    'tasks': ListChecks,
    'insights': TrendingUp,
    'google-ads-overview': Globe,
    'google-ads-campaigns': Rocket,
    'google-ads-optimization': TrendingUp,
    'customer-intelligence': Crosshair,
    'ai-insights': Sparkles,
    'time-tracking': Clock,
    'expenses': Receipt,
    'feedback': MessageSquarePlus,
    'customers': BarChart3,
    'products': Package,
    'documents': FileText,
    'closing-assistant': Zap,
    'engagements': Briefcase,
    'team-skills': Users2,
    'stream': BarChart3,
    'emails': Mail,
    'brandbook': Palette,
    'automate': Bot,
    'business-plan': LineChart,
    'material': FileText,
    'company-profile': Building2,
    'quick-select': Crosshair,
    'meeting-prep': Users2,
    'software-compliance': ShieldCheck,
    'echo-brain': Brain,
    'development': Code2,
  };

  const labelMap: Record<string, string> = {
    'dashboard': t('items.dashboard'),
    'guide': t('items.guide'),
    'leads': t('items.leads'),
    'companies': t('items.companies'),
    'opportunities': t('items.opportunities'),
    'tasks': t('items.tasks'),
    'insights': t('items.insights'),
    'google-ads-overview': t('items.googleAdsOverview'),
    'google-ads-campaigns': t('items.googleAdsCampaigns'),
    'google-ads-optimization': t('items.googleAdsOptimization'),
    'customer-intelligence': t('items.customerIntelligence'),
    'ai-insights': t('items.aiInsights'),
    'time-tracking': t('items.timeTracking'),
    'expenses': t('items.expenses'),
    'feedback': t('items.feedback'),
    'customers': t('items.customers'),
    'products': t('items.products'),
    'documents': t('items.documents'),
    'closing-assistant': t('items.closingAssistant'),
    'engagements': t('items.engagements'),
    'team-skills': t('items.teamSkills'),
    'stream': t('items.stream'),
    'emails': t('items.emails'),
    'brandbook': t('items.brandbook'),
    'automate': t('items.automate'),
    'business-plan': t('items.businessPlan'),
    'material': t('items.material'),
    'company-profile': t('items.companyProfile'),
    'quick-select': t('items.quickSelect'),
    'meeting-prep': t('items.meetingPrep'),
    'software-compliance': t('items.softwareCompliance'),
    'echo-brain': 'Echo-Brain',
    'development': t('items.development'),
  };

  const specialIds = new Set(['ai-insights', 'quick-select', 'meeting-prep']);
  // Distinct from footer "Feedback melden" button:
  // this ID is the main navigation entry that opens the Ticket-System page.
  const alwaysVisibleIds = new Set(['feedback', 'guide']);

  const allItemDefs: NavItemDef[] = useMemo(() => [
    { id: 'dashboard', defaultGroup: 'primary', defaultOrder: 0 },
    { id: 'guide', defaultGroup: 'primary', defaultOrder: 1 },
    { id: 'leads', defaultGroup: 'primary', defaultOrder: 2 },
    { id: 'companies', defaultGroup: 'primary', defaultOrder: 3 },
    { id: 'opportunities', defaultGroup: 'primary', defaultOrder: 4 },
    { id: 'tasks', defaultGroup: 'primary', defaultOrder: 5 },
    { id: 'insights', defaultGroup: 'primary', defaultOrder: 6 },
    { id: 'google-ads-overview', defaultGroup: 'secondary', defaultOrder: 0 },
    { id: 'google-ads-campaigns', defaultGroup: 'secondary', defaultOrder: 1 },
    { id: 'google-ads-optimization', defaultGroup: 'secondary', defaultOrder: 2 },
    { id: 'customer-intelligence', defaultGroup: 'secondary', defaultOrder: 3 },
    { id: 'ai-insights', defaultGroup: 'secondary', defaultOrder: 4 },
    { id: 'time-tracking', defaultGroup: 'secondary', defaultOrder: 5 },
    { id: 'expenses', defaultGroup: 'secondary', defaultOrder: 6 },
    { id: 'feedback', defaultGroup: 'secondary', defaultOrder: 7 },
    { id: 'software-compliance', defaultGroup: 'secondary', defaultOrder: 8 },
    { id: 'customers', defaultGroup: 'more', defaultOrder: 0 },
    { id: 'products', defaultGroup: 'more', defaultOrder: 1 },
    { id: 'documents', defaultGroup: 'more', defaultOrder: 2 },
    { id: 'closing-assistant', defaultGroup: 'more', defaultOrder: 3 },
    { id: 'engagements', defaultGroup: 'more', defaultOrder: 4 },
    { id: 'team-skills', defaultGroup: 'more', defaultOrder: 5 },
    { id: 'stream', defaultGroup: 'more', defaultOrder: 6 },
    { id: 'emails', defaultGroup: 'more', defaultOrder: 7 },
    { id: 'brandbook', defaultGroup: 'more', defaultOrder: 8 },
    { id: 'automate', defaultGroup: 'more', defaultOrder: 9 },
    { id: 'business-plan', defaultGroup: 'more', defaultOrder: 10 },
    { id: 'material', defaultGroup: 'more', defaultOrder: 11 },
    { id: 'company-profile', defaultGroup: 'more', defaultOrder: 12 },
    { id: 'quick-select', defaultGroup: 'more', defaultOrder: 13 },
    { id: 'meeting-prep', defaultGroup: 'more', defaultOrder: 14 },
    { id: 'echo-brain', defaultGroup: 'secondary', defaultOrder: 9 },
    { id: 'development', defaultGroup: 'secondary', defaultOrder: 10 },
  ], []);

  const canReadItem = useCallback((id: string) => {
    if (alwaysVisibleIds.has(id)) return true;
    if (specialIds.has(id)) return true;
    return permissions ? canRead(id as PageKey) : true;
  }, [permissions, canRead]);

  const { availableIds } = useNavigationConfig(currentUserId, allItemDefs, canReadItem);

  const resolveItem = useCallback((id: string): NavItem => ({
    id,
    icon: iconMap[id] || LayoutDashboard,
    label: labelMap[id] || id,
    special: specialIds.has(id),
  }), [iconMap, labelMap]);

  const availableNavItems = useMemo(
    () => availableIds.map(resolveItem),
    [availableIds, resolveItem],
  );

  // Top-level grouped navigation: each group shows if at least one of its
  // sub-views is accessible, and navigates to its first accessible sub-view.
  const groupNavItems = useMemo(() => {
    return NAV_GROUPS.map(group => {
      const accessibleIds = group.items.map(i => i.id).filter(canReadItem);
      return {
        id: group.id,
        icon: group.icon,
        label: group.label,
        target: accessibleIds[0],
        itemIds: group.items.map(i => i.id),
      };
    }).filter(g => !!g.target);
  }, [canReadItem]);

  // ─── Desktop Nav Item (icon-only or icon+label) ───────────

  const DesktopNavItem = ({ item, active }: { item: NavItem; active?: boolean }) => {
    const isActive = active !== undefined ? active : activeTab === item.id;
    const Icon = item.icon;
    const handleClick = (e: React.MouseEvent) => {
      e.preventDefault();
      handleNav(item.id);
    };

    if (isExpanded) {
      return (
        <a
          href={`#${item.id}`}
          onClick={handleClick}
          className={[
            'w-full flex items-center gap-3 px-3 py-2 rounded-sm no-underline',
            'cursor-pointer transition-all duration-200 ease-spring-smooth',
            isActive
              ? item.special
                ? 'bg-[var(--accent-medium)] text-accent'
                : 'bg-[var(--accent-soft)] text-accent'
              : 'text-txt-tertiary hover:text-accent hover:bg-bg-card',
          ].join(' ')}
        >
          <Icon className="w-5 h-5 shrink-0" strokeWidth={1.5} />
          <span className="text-[13px] font-medium truncate">{item.label}</span>
        </a>
      );
    }

    return (
      <a
        href={`#${item.id}`}
        onClick={handleClick}
        title={item.label}
        className={[
          'w-[42px] h-[42px] rounded-sm no-underline',
          'grid place-items-center',
          'cursor-pointer transition-all duration-200 ease-spring-smooth',
          isActive
            ? item.special
              ? 'bg-[var(--accent-medium)] text-accent'
              : 'bg-[var(--accent-soft)] text-accent'
            : 'text-txt-tertiary hover:text-accent hover:bg-bg-card',
        ].join(' ')}
      >
        <Icon className="w-5 h-5" strokeWidth={1.5} />
      </a>
    );
  };

  // ─── Mobile Nav Item ────────────────────────────────────────

  const MobileNavItem = ({ item, active }: { item: NavItem; active?: boolean }) => {
    const isActive = active !== undefined ? active : activeTab === item.id;
    const Icon = item.icon;

    return (
      <button
        onClick={() => handleNav(item.id)}
        className={`
          w-full flex items-center gap-3 px-4 py-3 rounded-lg
          transition-all duration-200 ease-spring-smooth
          ${isActive
            ? 'bg-[var(--accent-soft)] text-accent'
            : 'text-txt-secondary hover:text-txt hover:bg-bg-card'
          }
        `.trim().replace(/\s+/g, ' ')}
      >
        <Icon className="w-5 h-5" strokeWidth={1.5} />
        <span className="text-[13px] font-medium">{item.label}</span>
      </button>
    );
  };

  // ─── User Initials ─────────────────────────────────────────

  const initials = currentUserName
    ? currentUserName.split(' ').map(n => n[0]).join('').slice(0, 2).toUpperCase()
    : 'U';

  // ─── Desktop Sidebar ──────────────────────────────────────

  const signOutModal = (
    <Modal
      isOpen={signOutModalOpen}
      onClose={() => setSignOutModalOpen(false)}
      title={tc('signOutConfirmTitle')}
      size="sm"
      footer={
        <>
          <Button variant="ghost" onClick={() => setSignOutModalOpen(false)}>
            {tc('cancel')}
          </Button>
          <Button variant="danger" onClick={executeSignOut}>
            {tc('signOutConfirm')}
          </Button>
        </>
      }
    >
      <p className="text-sm text-txt-secondary leading-relaxed">{tc('signOutConfirmMessage')}</p>
    </Modal>
  );

  if (!isMobile) {
    return (
      <>
      <aside
        style={{ width: isExpanded ? 220 : 68 }}
        className="
          bg-bg-raised border-r border-default
          fixed top-0 left-0 bottom-0 z-50
          flex flex-col
          py-5 gap-1
          transition-[width] duration-300 ease-spring-smooth
        "
      >
        <div
          className={`flex flex-col gap-1 flex-1 min-h-0 overflow-y-auto ${isExpanded ? 'px-3' : 'items-center'}`}
          style={{ scrollbarWidth: 'thin' }}
        >
          {/* Logo row */}
          <div className={`flex items-center ${isExpanded ? 'gap-3 mb-5 px-1' : 'justify-center mb-7'}`}>
            <div
              className="w-10 h-10 rounded-sm bg-gradient-to-br from-accent to-info grid place-items-center relative overflow-hidden cursor-pointer shrink-0"
              onClick={() => handleNav('dashboard')}
            >
              <span className="text-txt-inverted font-bold text-sm relative z-[1]">B</span>
              <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent animate-shine" />
            </div>
            {isExpanded && (
              <div className="min-w-0">
                <h1 className="font-display text-base font-bold text-txt leading-tight truncate">{tc('appName')}</h1>
                <p className="text-[11px] text-txt-muted truncate">{tc('appTagline')}</p>
              </div>
            )}
          </div>

          {/* Grouped navigation (top level) */}
          {isExpanded && (
            <p className="font-mono text-[10px] font-medium text-txt-tertiary uppercase tracking-[0.08em] px-1 mb-1">Navigation</p>
          )}
          {groupNavItems.map(group => (
            <DesktopNavItem
              key={group.id}
              item={{ id: group.target as string, icon: group.icon, label: group.label }}
              active={group.itemIds.includes(activeTab)}
            />
          ))}

          {/* More button – shows available/hidden items */}
          {false && availableNavItems.length > 0 && (
            <div className="relative" ref={moreButtonRef}>
              <button
                onClick={() => setMoreOpen(!moreOpen)}
                title={t('moreTools')}
                className={[
                  isExpanded
                    ? 'w-full flex items-center gap-3 px-3 py-2 rounded-sm'
                    : 'w-[42px] h-[42px] rounded-sm grid place-items-center',
                  'cursor-pointer transition-all duration-200',
                  moreOpen ? 'text-txt bg-bg-card' : 'text-txt-muted hover:text-accent hover:bg-bg-card',
                ].join(' ')}
              >
                <MoreHorizontal className="w-5 h-5 shrink-0" strokeWidth={1.5} />
                {isExpanded && <span className="text-[13px] font-medium text-txt-secondary truncate">{t('moreTools')}</span>}
              </button>

              {moreOpen && (
                <>
                  <div className="fixed inset-0 z-40" onClick={() => setMoreOpen(false)} />
                  <div
                    ref={morePopoverRef}
                    className="
                      fixed z-50
                      bg-bg-card border border-default rounded-lg
                      shadow-heavy
                      py-2 min-w-[200px]
                      animate-spring-in
                      overflow-y-auto
                      overscroll-contain
                    "
                    style={{ scrollbarWidth: 'thin' }}
                  >
                    {availableNavItems.map(item => {
                      const isActive = activeTab === item.id;
                      const Icon = item.icon;
                      return (
                        <a
                          key={item.id}
                          href={`#${item.id}`}
                          onClick={(e) => { e.preventDefault(); handleNav(item.id); setMoreOpen(false); }}
                          className={[
                            'w-full flex items-center gap-3 px-4 py-2.5 no-underline',
                            'text-[13px] font-medium',
                            'transition-colors duration-150',
                            isActive
                              ? 'text-accent bg-[var(--accent-soft)]'
                              : 'text-txt-secondary hover:text-txt hover:bg-bg-surface',
                          ].join(' ')}
                        >
                          <Icon className="w-4 h-4 shrink-0" strokeWidth={1.5} />
                          <span className="truncate">{item.label}</span>
                        </a>
                      );
                    })}
                  </div>
                </>
              )}
            </div>
          )}
        </div>

        {/* Bottom section */}
        <div className={`flex flex-col gap-1 shrink-0 ${isExpanded ? 'px-3' : 'items-center'}`}>
          {import.meta.env.DEV && (
            <button
              onClick={() => {
                const el = document.getElementById('task-debug-toggle');
                if (el) el.click();
              }}
              title="Debug"
              className={[
                isExpanded
                  ? 'w-full flex items-center gap-3 px-3 py-1.5 rounded-sm'
                  : 'w-[34px] h-[34px] rounded-sm grid place-items-center',
                'text-txt-muted cursor-pointer transition-all duration-200 hover:text-accent hover:bg-bg-card',
              ].join(' ')}
            >
              <Bug className="w-[18px] h-[18px] shrink-0" strokeWidth={1.5} />
              {isExpanded && <span className="text-[13px] font-medium text-txt-secondary truncate">Debug</span>}
            </button>
          )}
          {/* Theme Toggle */}
          {canToggleBaseTheme && (
            <button
              onClick={toggleBaseTheme}
              title={baseTheme === 'dark' ? t('lightMode') : t('darkMode')}
              className={[
                isExpanded
                  ? 'w-full flex items-center gap-3 px-3 py-1.5 rounded-sm'
                  : 'w-[34px] h-[34px] rounded-sm grid place-items-center',
                'text-txt-muted cursor-pointer transition-all duration-200 hover:text-accent hover:bg-bg-card',
              ].join(' ')}
            >
              {baseTheme === 'dark'
                ? <Sun className="w-[18px] h-[18px] shrink-0" strokeWidth={1.5} />
                : <Moon className="w-[18px] h-[18px] shrink-0" strokeWidth={1.5} />
              }
              {isExpanded && (
                <span className="text-[13px] font-medium text-txt-secondary truncate">
                  {baseTheme === 'dark' ? t('lightMode') : t('darkMode')}
                </span>
              )}
            </button>
          )}

          {/* Demo Mode Toggle */}
          <button
            onClick={toggleDemoMode}
            title={isDemoMode ? t('demoModeOn') : t('demoMode')}
            className={[
              isExpanded
                ? 'w-full flex items-center gap-3 px-3 py-1.5 rounded-sm'
                : 'w-[34px] h-[34px] rounded-sm grid place-items-center',
              'cursor-pointer transition-all duration-200',
              isDemoMode
                ? 'text-warning bg-warning-soft hover:bg-warning-soft'
                : 'text-txt-muted hover:text-accent hover:bg-bg-card',
            ].join(' ')}
          >
            {isDemoMode
              ? <EyeOff className="w-[18px] h-[18px] shrink-0" strokeWidth={1.5} />
              : <Eye className="w-[18px] h-[18px] shrink-0" strokeWidth={1.5} />
            }
            {isExpanded && (
              <span className="text-[13px] font-medium truncate">
                {isDemoMode ? t('demoModeOn') : t('demoMode')}
              </span>
            )}
          </button>

          {/* Feedback melden (Modal) — not the Ticket-System nav entry above */}
          {onGiveFeedback && (
            <button
              type="button"
              onClick={onGiveFeedback}
              title={t('giveFeedback')}
              className={[
                isExpanded
                  ? 'w-full flex items-center gap-3 px-3 py-1.5 rounded-sm'
                  : 'w-[34px] h-[34px] rounded-sm grid place-items-center',
                'text-txt-muted cursor-pointer transition-all duration-200 ease-spring-smooth hover:text-accent hover:bg-bg-card',
              ].join(' ')}
            >
              <MessageSquarePlus className="w-[18px] h-[18px] shrink-0" strokeWidth={1.5} />
              {isExpanded && (
                <span className="text-[13px] font-medium text-txt-secondary truncate">{t('giveFeedback')}</span>
              )}
            </button>
          )}

          {/* Separator */}
          <div className={`${isExpanded ? '' : 'w-7'} h-px bg-[var(--border-default)] my-1 ${isExpanded ? 'mx-1' : ''}`} />

          {/* Settings */}
          <DesktopNavItem item={{ id: 'settings', icon: Settings2, label: tc('settings') }} />

          {/* Logout */}
          <button
            onClick={() => setSignOutModalOpen(true)}
            title={tc('signOut')}
            className={[
              isExpanded
                ? 'w-full flex items-center gap-3 px-3 py-1.5 rounded-sm'
                : 'w-[34px] h-[34px] rounded-sm grid place-items-center',
              'text-danger cursor-pointer transition-all duration-200 hover:bg-danger-soft',
            ].join(' ')}
          >
            <LogOut className="w-[18px] h-[18px] shrink-0" strokeWidth={1.5} />
            {isExpanded && <span className="text-[13px] font-medium truncate">{tc('signOut')}</span>}
          </button>

          {/* User Avatar row */}
          <div className={`flex items-center ${isExpanded ? 'gap-3 px-1 mt-1 mb-2' : 'justify-center mt-1 mb-2'}`}>
            <button
              onClick={() => handleNav('settings')}
              title={currentUserName || t('profile')}
              className="
                w-[34px] h-[34px] rounded-pill shrink-0
                bg-gradient-to-br from-pastel-lavender to-pastel-mint
                grid place-items-center
                font-display font-bold text-[11px] text-[#2E1065]
                cursor-pointer
                transition-transform duration-200 ease-spring-bounce
                hover:scale-110
              "
            >
              {initials}
            </button>
            {isExpanded && (
              <span className="text-[13px] font-medium text-txt-secondary truncate">
                {currentUserName || t('profile')}
              </span>
            )}
          </div>

          {/* Expand/Collapse toggle */}
          <button
            onClick={() => setExpanded(!isExpanded)}
            title={isExpanded ? t('collapseMenu') : t('expandMenu')}
            className={[
              isExpanded
                ? 'w-full flex items-center gap-3 px-3 py-2 rounded-sm'
                : 'w-[42px] h-[42px] rounded-sm grid place-items-center',
              'cursor-pointer transition-all duration-200 ease-spring-smooth',
              'text-txt-muted hover:text-accent hover:bg-bg-card',
            ].join(' ')}
          >
            {isExpanded
              ? <ChevronsLeft className="w-5 h-5 shrink-0" strokeWidth={1.5} />
              : <ChevronsRight className="w-5 h-5" strokeWidth={1.5} />
            }
            {isExpanded && <span className="text-[13px] font-medium text-txt-secondary truncate">{t('collapse')}</span>}
          </button>
        </div>

      </aside>
      {signOutModal}
      </>
    );
  }

  // ─── Mobile Sidebar (Overlay) ──────────────────────────────

  return (
    <>
      {/* Backdrop */}
      {isOpen && (
        <div
          className="fixed inset-0 bg-black/60 backdrop-blur-sm z-40 animate-fade-in"
          onClick={onClose}
        />
      )}

      {/* Slide-in panel */}
      <aside className={`
        fixed top-0 left-0 bottom-0 z-50
        w-72 bg-bg-raised
        border-r border-default
        transform transition-transform duration-300 ease-spring-smooth
        ${isOpen ? 'translate-x-0' : '-translate-x-full'}
        flex flex-col
        overflow-y-auto
      `}>
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-subtle">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-sm bg-gradient-to-br from-accent to-info grid place-items-center">
              <span className="text-txt-inverted font-bold text-sm">B</span>
            </div>
            <div>
              <h1 className="font-display text-base font-bold text-txt">{tc('appName')}</h1>
              <p className="text-[11px] text-txt-muted">{tc('appTagline')}</p>
            </div>
          </div>
          <button onClick={onClose} className="w-8 h-8 rounded-pill grid place-items-center text-txt-tertiary hover:text-txt hover:bg-bg-card">
            <X className="w-4 h-4" strokeWidth={1.5} />
          </button>
        </div>

        {/* Navigation */}
        <nav className="flex-1 p-3 space-y-1">
          <div className="mb-2">
            <p className="font-mono text-[10px] font-medium text-txt-muted uppercase tracking-[0.08em] px-4 mb-2">Navigation</p>
            {groupNavItems.map(group => (
              <MobileNavItem
                key={group.id}
                item={{ id: group.target as string, icon: group.icon, label: group.label }}
                active={group.itemIds.includes(activeTab)}
              />
            ))}
          </div>

          {false && availableNavItems.length > 0 && (
            <>
              <div className="mx-4 h-px bg-[var(--border-default)]" />
              <div className="pt-2">
                <p className="font-mono text-[10px] font-medium text-txt-muted uppercase tracking-[0.08em] px-4 mb-2">{t('moreTools')}</p>
                {availableNavItems.map(item => <MobileNavItem key={item.id} item={item} />)}
              </div>
            </>
          )}
        </nav>

        {/* Footer */}
        <div className="p-4 border-t border-subtle space-y-2">
          {canToggleBaseTheme && (
            <button
              onClick={toggleBaseTheme}
              className="w-full flex items-center gap-3 px-4 py-3 rounded-lg text-txt-secondary hover:text-txt hover:bg-bg-card transition-all duration-200"
            >
              {baseTheme === 'dark'
                ? <Sun className="w-5 h-5" strokeWidth={1.5} />
                : <Moon className="w-5 h-5" strokeWidth={1.5} />
              }
              <span className="text-[13px] font-medium">
                {baseTheme === 'dark' ? t('lightMode') : t('darkMode')}
              </span>
            </button>
          )}
          <button
            type="button"
            onClick={toggleDemoMode}
            className={[
              'w-full flex items-center gap-3 px-4 py-3 rounded-lg transition-all duration-200',
              isDemoMode
                ? 'text-warning bg-warning-soft'
                : 'text-txt-secondary hover:text-txt hover:bg-bg-card',
            ].join(' ')}
          >
            {isDemoMode
              ? <EyeOff className="w-5 h-5 shrink-0" strokeWidth={1.5} />
              : <Eye className="w-5 h-5 shrink-0" strokeWidth={1.5} />
            }
            <span className="text-[13px] font-medium">
              {isDemoMode ? t('demoModeOn') : t('demoMode')}
            </span>
          </button>
          {onGiveFeedback && (
            <button
              type="button"
              onClick={() => {
                onGiveFeedback();
                onClose();
              }}
              className="w-full flex items-center gap-3 px-4 py-3 rounded-lg text-txt-secondary hover:text-txt hover:bg-bg-card transition-all duration-200"
            >
              <MessageSquarePlus className="w-5 h-5 shrink-0" strokeWidth={1.5} />
              <span className="text-[13px] font-medium">{t('giveFeedback')}</span>
            </button>
          )}
          <MobileNavItem item={{ id: 'settings', icon: Settings2, label: tc('settings') || 'Settings' }} />
          <button
            onClick={() => setSignOutModalOpen(true)}
            className="w-full flex items-center gap-3 px-4 py-3 rounded-lg text-danger hover:bg-danger-soft transition-colors"
          >
            <LogOut className="w-5 h-5" strokeWidth={1.5} />
            <span className="text-[13px] font-medium">{tc('signOut')}</span>
          </button>
        </div>

      </aside>
      {signOutModal}
    </>
  );
};

export default SidebarNew;
